import { debounce } from 'lodash-es'
import { gsap } from 'gsap'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

const setupSearch = () => {
  const el = document.querySelector('[data-component="search"]')
  const content = el.querySelector('[data-content]')
  const closeBtn = el.querySelector('[data-close]')
  const input = el.querySelector('[data-input]')
  const loader = el.querySelector('[data-loader]')
  const language = window.location.pathname.split('/')[1] === 'en' ? 'en' : 'da'

  let openBtn = document.querySelector('[data-component="search-open"]')
  let casesContainer = undefined
  let casesTitle = undefined
  let casesList = undefined
  let pagesContainer = undefined
  let pagesTitle = undefined
  let pagesList = undefined

  let defaultCases = undefined
  let defaultPages = undefined

  const transitionIn = gsap
    .timeline({
      paused: true,
    })
    .set(el, {
      display: 'flex',
    })
    .add('start')
    .fromTo(
      el,
      {
        yPercent: 100,
      },
      {
        yPercent: 0,
        duration: 0.8,
        ease: 'power3.out',
      },
    )
    .fromTo(
      content,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        ease: 'expo.inOut',
        duration: 0.8,
      },
      'start+=0.5',
    )
    .eventCallback('onStart', () => {
      disableBodyScroll(el)
    })
    .eventCallback('onComplete', () => {
      input.focus()
    })

  const transitionOut = gsap
    .timeline({
      paused: true,
    })
    .add('start')
    .to(content, {
      opacity: 0,
      ease: 'power3.in',
      duration: 0.4,
    })
    .to(
      el,
      {
        yPercent: -100,
        ease: 'expo.in',
        duration: 0.6,
      },
      'start+=0.3',
    )
    .eventCallback('onComplete', () => {
      enableBodyScroll(el)
    })

  function getRefs() {
    casesContainer = el.querySelector('[data-cases]')
    casesTitle = casesContainer.querySelector('[data-title]')
    casesList = casesContainer.querySelector('[data-list]')
    pagesContainer = el.querySelector('[data-pages]')
    pagesTitle = pagesContainer.querySelector('[data-title]')
    pagesList = pagesContainer.querySelector('[data-list]')

    defaultCases = casesContainer.innerHTML
    defaultPages = pagesContainer.innerHTML
  }

  function setDefaultCases() {
    casesContainer.innerHTML = defaultCases
  }

  function setDefaultPages() {
    pagesContainer.innerHTML = defaultPages
  }

  function open() {
    transitionIn.seek(0).play()
  }

  function close() {
    transitionOut.seek(0).play()
  }

  function showLoader() {
    return new Promise((res) => {
      gsap.fromTo(
        loader,
        {
          y: 10,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'expo.inOut',
          onComplete: res,
        },
      )
    })
  }

  function hideLoader() {
    return new Promise((res) => {
      gsap.to(loader, {
        y: -10,
        opacity: 0,
        duration: 0.4,
        ease: 'expo.inOut',
        onComplete: res,
      })
    })
  }

  function hideResults() {
    return new Promise((res) => {
      gsap.timeline().to([casesContainer, pagesContainer], {
        y: 50,
        opacity: 0,
        ease: 'expo.out',
        duration: 0.6,
        onComplete: res,
      })
    })
  }

  function showResults() {
    return new Promise((res) => {
      gsap.timeline().to([casesContainer, pagesContainer], {
        y: 0,
        opacity: 1,
        ease: 'expo.out',
        duration: 1,
        onComplete: res,
      })
    })
  }

  async function fetchResults(evt) {
    showLoader()
    await hideResults()

    if (evt.target.value.length === 0) {
      window.requestAnimationFrame(() => {
        setDefaultCases()
        setDefaultPages()
        getRefs()

        window.requestAnimationFrame(() => {
          showResults()
          hideLoader()
        })
      })
    } else {
      window
        .fetch(`${language === 'en' ? '/en' : ''}/api/gql`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: `
          query Search {
            pages: entries(search: "${evt.target.value}", section: ["general", "careers", "construction", "news", "contact"]) {
              title
              url
            }
            cases: entries(search: "${evt.target.value}", section: ["cases"]) {
              title
              url
              ... on cases_case_Entry {
                id
                caseCity
                image {
                  ... on images_Asset {
                    id
                    optimizedImages {
                      src
                      srcset
                      srcsetWebp
                    }
                  }
                }
                caseCategory {
                  title
                }
              }
            }
          }
        `,
          }),
        })
        .then((response) => response.json())
        .then(async (response) => {
          await updateCases(response.data.cases)
          await updatePages(response.data.pages)

          showResults()
          hideLoader()
        })
    }
  }

  function updateCases(items) {
    return new Promise((res) => {
      window.requestAnimationFrame(() => {
        casesTitle.innerHTML = language === 'en' ? 'Cases' : 'Referencer'
        casesList.innerHTML = ''

        if (items.length > 0) {
          const fragment = document.createDocumentFragment()

          items.forEach((item) => {
            const li = document.createElement('li')
            li.classList.add('col-span-1')

            li.innerHTML = `
            <a class="block space-y-4" href="${item.url}">
              <div class="aspect-w-16 aspect-h-9">
                <picture>
                  <source srcset="${item.image[0].optimizedImages.srcsetWebp}"
                          sizes="100vw"
                          type="image/webp" />
                  <img src="${item.image[0].optimizedImages.src}}"
                       srcset="${item.image[0].optimizedImages.srcset}"
                       sizes="100vw" />
                </picture>
              </div>
              <div class="text-sm">
                <h4 class="font-bold uppercase">${item.title}</h4>
                <p>${item.caseCategory
                  .map((cat) => {
                    return cat.title
                  })
                  .join(' / ')}<br />(${item.caseCity})</p>
              </div>
            </a>
          `

            fragment.appendChild(li)
            window.webkitRequestAnimationFrame(res)
          })

          casesList.appendChild(fragment)
        } else {
          const li = document.createElement('li')
          li.classList.add('text-base', 'md:text-lg', 'text-gray')
          li.innerHTML = 'Intet match...'

          window.requestAnimationFrame(() => {
            casesList.appendChild(li)
            window.webkitRequestAnimationFrame(res)
          })
        }
      })
    })
  }

  function updatePages(items) {
    return new Promise((res) => {
      window.requestAnimationFrame(() => {
        pagesList.innerHTML = ''
        pagesTitle.innerHTML = language === 'en' ? 'Pages' : 'Sider'

        if (items.length > 0) {
          const fragment = document.createDocumentFragment()

          items.forEach((i) => {
            const li = document.createElement('li')
            const a = document.createElement('a')

            ;(a.href = i.url), (a.innerHTML = i.title)

            li.appendChild(a)
            fragment.appendChild(li)
          })

          window.requestAnimationFrame(() => {
            pagesList.appendChild(fragment)
            window.webkitRequestAnimationFrame(res)
          })
        } else {
          const li = document.createElement('li')
          li.classList.add('text-gray')
          li.innerHTML = 'Intet match...'

          window.requestAnimationFrame(() => {
            pagesList.appendChild(li)
            window.webkitRequestAnimationFrame(res)
          })
        }
      })
    })
  }

  function init() {
    input.addEventListener('input', debounce(fetchResults, 500), false)
    openBtn.addEventListener('click', open, false)
    closeBtn.addEventListener('click', close, false)

    window.addEventListener(
      'navigation:out',
      () => {
        enableBodyScroll(el)
      },
      false,
    )

    getRefs()
  }

  init()
}

export default setupSearch
